import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { MultiSelect } from 'react-multi-select-component';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  Bar,
  BarChart,
  Brush,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { colorPalette, SHORT_DAY_NAME_FORMAT } from '../../constants/constants';
import { useStore } from '../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import Grid from '../../shared-components/Grid/Grid';
import { FilterIcon, RefreshIcon } from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import './active-tray-management.css';
import FilterComponent from './components/process-wise-chart-filter';
import { IProcessWiseChartFilter } from '../../models/IProcessWiseChartFilter';
import { ProcessWiseChartFilterInitialState } from '../../core/initialState/filter-initial-state';
import FilterPopupHOC from '../../shared-components/hoc/filter-popup-hoc/filter-popup-hoc';
const ProcessWiseChart = (props: any) => {
  const [chartHeight, setChartHeight] = useState(250);
  const [selected, setSelected] = useState([]);
  const [allowedColumns, setAllowedColumns] = useState([]);

  const [filterObj, setFilterObj] = useState<IProcessWiseChartFilter>(
    ProcessWiseChartFilterInitialState
  );
  const [isFilterCleared, setIsFilterCleared] = useState(true);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
const [brushRange, setBrushRange] = useState<{
  startIndex?: number;
  endIndex?: number;
}>({});
  const calculatedWidth = allowedColumns.length * 10 * 21 + 210;
  const { activeTrayManagementStore } = useStore();
  const {
    GetProcessWiseChartListService,
    getProcessWiseChartListServiceState,
    chartHeaders,
    chartData,
    chartProcessMultiSelectOptions,
    allChartProcesses,
  } = activeTrayManagementStore;

  const headers = chartHeaders;
  const data = chartData;
  const initialSelection = allChartProcesses;

  useEffect(() => {
    getProcessWiseChartListServiceHandler(0);
  }, []);

  useEffect(() => {
    getProcessWiseChartListServiceHandler(filterObj.SubContractor);
  }, [chartHeight]);

  const getProcessWiseChartListServiceHandler = (subContractor) => {
    const offsetInMinutes = moment().utcOffset();
    GetProcessWiseChartListService(
      Number(getTenantIdFromLocalStorage()),
      offsetInMinutes,
      subContractor
    );
  };

  const filteredChartData = data.filter((item) =>
    allowedColumns.includes(item.name)
  );

  // Convert data for Bootstrap Table
  const tableData = filteredChartData.map((row) => {
    let rowData: any = { name: row.name, processName: row.processName };

    headers.forEach((header, index) => {
      rowData[header] = row.values[index];
    });
    return rowData;
  });

  const handleSelectionChange = async (selectedOptions) => {
    if (selectedOptions.length === 0) {
      setSelected([...initialSelection]);
      setAllowedColumns([]);
    } else {
      setSelected(selectedOptions);
      setAllowedColumns([...selectedOptions.map((item) => item.value)]);
    }
  };

  useEffect(() => {
    if (chartProcessMultiSelectOptions.length > 0 && selected.length === 0) {
      setSelected(chartProcessMultiSelectOptions);
      setAllowedColumns([
        ...chartProcessMultiSelectOptions.map((item) => item.value),
      ]);
    }
  }, [chartProcessMultiSelectOptions]);

  const columns = [
    {
      dataField: 'name',
      text: '#',
      headerStyle: { backgroundColor: '#007bff', color: 'white' },
      classes: 'font-weight-bold',
      title: (cell: string, row: any) => {
        return row.processName;
      },
      headerFormatter: () => {
        return (
          // <MultiSelect
          //   className="product-select filter-table"
          //   hasSelectAll={true}
          //   options={chartProcessMultiSelectOptions}
          //   value={selected}
          //   isOpen={true}
          //   labelledBy={''}
          //   onChange={handleSelectionChange}
          //   overrideStrings={{
          //     search: formatMessage('search'),
          //   }}
          //   valueRenderer={() => (
          //     <>
          //       <FilterIcon />
          //     </>
          //   )}
          //   ClearSelectedIcon={null}
          // />
          <></>
        );
      },
    },
    ...headers.map((header) => ({
      dataField: header,
      text: dateFormatterMMdd(header),
      headerStyle: { backgroundColor: '#007bff', color: 'white' },
    })),
  ];
const chartDataFormatter = useMemo(() => {
  return headers.map((date, index) => {
    const rowData: any = { date: dateFormatterMMdd(date, false) };

    data.forEach((item) => {
      if (allowedColumns.includes(item.name)) {
        rowData[item.name] = item.values[index];
      }
    });

    return rowData;
  });
}, [headers, data, allowedColumns]);

  function dateFormatterMMdd(dateString: string, isAllowed = true) {
    const [year, month, day] = dateString.split('-');
    const date = new Date(`${year}-${month}-${day}`);
    const result = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
    });

    if (isAllowed) {
      return result === 'Invalid Date'
        ? 'Prev'
        : `${result} (${formatMessage(moment(dateString).format(SHORT_DAY_NAME_FORMAT))})`;
    }

    return result === 'Invalid Date' ? 'Prev' : result;
  }

  const refreshClickHandler = () => {
    getProcessWiseChartListServiceHandler(filterObj.SubContractor);
  };

  // Step 2: Create a color map (using consistent ordering)
  const getColorMap = (keys: string[]): Record<string, string> => {
    const colorMap: Record<string, string> = {};
    keys.forEach((key, index) => {
      colorMap[key] = colorPalette[index % colorPalette.length];
    });
    return colorMap;
  };

  // Only call this once and reuse
  const uniqueKeys = Array.from(new Set(data.map((d) => d.name))); // stable key list
  const colorMap = getColorMap(uniqueKeys);

  const filterHandler = (object: IProcessWiseChartFilter) => {
    const filters = {
      ...object,
    };
    getProcessWiseChartListServiceHandler(filters.SubContractor);
    setFilterObj({ ...filters });
    props.filterPopupToggleHandler();
    setIsFilterCleared(false);
  };

  const filterParams = (
    obj: IProcessWiseChartFilter,
    isFilterCleared: boolean
  ) => {
    if (isFilterCleared) {
      setFilterObj({ ...obj });
      getProcessWiseChartListServiceHandler(obj.SubContractor);
      setIsFilterCleared(true);
    } else {
      setFilterObj({ ...obj });
    }
    props.filterPopupToggleHandler();
  };

useEffect(() => {
  const storedRange = localStorage.getItem('processChartBrushRange');

  if (storedRange) {
    try {
      const parsed = JSON.parse(storedRange);
      setBrushRange(parsed);
    } catch (e) {
      console.error('Invalid brush range');
    }
  }
}, []);

useEffect(() => {
  if (!chartDataFormatter.length) return;

  setBrushRange((prev) => {
    if (
      prev.startIndex === undefined ||
      prev.endIndex === undefined
    ) {
      return prev;
    }

    const maxIndex = chartDataFormatter.length - 1;

    const newStart = Math.min(prev.startIndex, maxIndex);
    const newEnd = Math.min(prev.endIndex, maxIndex);

    // ✅ IMPORTANT: prevent unnecessary updates
    if (newStart === prev.startIndex && newEnd === prev.endIndex) {
      return prev;
    }

    return {
      startIndex: newStart,
      endIndex: newEnd,
    };
  });
}, [chartDataFormatter.length]); // ✅ ONLY length, not full object
  return (
    <>
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-lg-6 col-md-6 col-sm-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="process_wise_chart" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="home" />
                </li>
                <li
                  className="breadcrumb-item active"
                  aria-current="page"
                >
                  <FormattedMessage id="process_wise_chart" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-6">
            <ul className="add-list">
              <li>
                <FilterComponent
                  initialValues={filterObj}
                  id={0}
                  submitHandler={filterHandler}
                  filterParmas={filterParams}
                  onModalClosed={props.filterPopupToggleHandler}
                  isLoading={false}
                  isDisable={false}
                  isFilterCleared={isFilterCleared}
                  isFilterApplied={isFilterApplied}
                  setIsFilterApplied={setIsFilterApplied}
                />
              </li>
              <li title={formatMessage('refresh')}>
                <a
                  data-toggle="modal"
                  className="btn btn-light"
                  onClick={() => refreshClickHandler()}
                >
                  <RefreshIcon />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card card-page">
        {getProcessWiseChartListServiceState.inProgress && (
          <FormLoader
            loading={getProcessWiseChartListServiceState.inProgress}
          />
        )}
        {chartData && chartData.length > 0 && (
          <div className="tableDevices process-chart">
            <MultiSelect
              className="product-select filter-table chart-filter"
              hasSelectAll={true}
              options={chartProcessMultiSelectOptions}
              value={selected}
              // isOpen={true}
              labelledBy={''}
              onChange={handleSelectionChange}
              overrideStrings={{
                search: formatMessage('search'),
              }}
              valueRenderer={() => (
                <>
                  <FilterIcon />
                </>
              )}
              ClearSelectedIcon={null}
            />
            <PanelGroup direction="vertical">
              {/* Table Panel */}
              <Panel
                className="p-4 pb-0"
                defaultSize={50}
                minSize={30}
                maxSize={100}
              >
                <div className="tableDesign row-eq-height">
                  <div
                    className="table-responsive"
                    data-testid="table"
                  >
                    <Grid
                      keyField="name"
                      data={tableData}
                      columns={columns}
                    />
                  </div>
                </div>
              </Panel>
              {/* Resize Handle */}
              <PanelResizeHandle className="resize-handle" />
              {/* Chart Panel */}
              <Panel
                defaultSize={50}
                minSize={40}
                maxSize={100}
                onResize={(size) => {
                  setChartHeight(size);
                }}
              >
                <h4 className="chart-heading py-2 m-0 px-4">
                  {formatMessage('chart')}
                </h4>
                <div
                  className="processChartList "
                  style={{ height: chartHeight - 31 }}
                >
                  <ResponsiveContainer
                    width="100%"
                    //minWidth={calculatedWidth}
                    height={chartHeight * 4}
                  >
                    <BarChart
                      data={chartDataFormatter}
                      margin={{ top: 20, right: 10, left: 0, bottom: 5 }}
                    >
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      {data
                        .filter((row) => allowedColumns.includes(row.name))
                        .map((row, index) => (
                          <Bar
                            key={index}
                            dataKey={row.name}
                            // fill={`hsl(${index * 60}, 70%, 50%)`}
                            // fill={colorPalette[index % colorPalette.length]}
                            fill={colorMap[row.name]}
                            barSize={20}
                          />
                        ))}
<Brush
  dataKey="name"
  height={30}
  stroke="#8884d8"
  {...(brushRange.startIndex !== undefined &&
  brushRange.endIndex !== undefined
    ? {
        startIndex: brushRange.startIndex,
        endIndex: brushRange.endIndex,
      }
    : {})}
  onChange={(range) => {
    if (range?.startIndex !== undefined && range?.endIndex !== undefined) {
      const newRange = {
        startIndex: range.startIndex,
        endIndex: range.endIndex,
      };

      // console.log('Brush moving:', newRange);

      setBrushRange(newRange);

      localStorage.setItem(
        'processChartBrushRange',
        JSON.stringify(newRange)
      );
    }
  }}
/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </PanelGroup>
          </div>
        )}
      </div>
    </>
  );
};

export default FilterPopupHOC(observer(ProcessWiseChart));

